import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api-client.config';
import { queryKeys } from '../react-query.config';
import { toast } from 'sonner';
import type { GameTypeCode } from '@low6-pickem/sports-config';

export interface PayoutTemplateRow {
  id?: string;
  rank: number;
  rankTo?: number | null; // Range mode (DKPLAT-388): inclusive end of [rank..rankTo]
  cash: string;
  crowns: number;
  other?: string | null;
  profitBoostToken?: string | null;
  bonusBet?: string | null;
  createdAt?: string;
}

export interface PayoutTemplate {
  id: string;
  tenantId: string;
  title: string;
  totalCash: string;
  totalCrowns: number;
  format: string;
  mode: string; // 'position' | 'range' (DKPLAT-388)
  gameType: string;
  dailyHitThreshold: number;
  createdAt: string;
  updatedAt: string;
  rows: PayoutTemplateRow[];
}

interface GetPayoutTemplatesResponse {
  templates: PayoutTemplate[];
}

interface GetPayoutTemplateResponse {
  template: PayoutTemplate;
}

interface CreatePayoutTemplateBody {
  title: string;
  totalCash: string;
  totalCrowns: number;
  format: 'split' | 'fixed';
  mode?: 'position' | 'range';
  gameType: GameTypeCode;
  dailyHitThreshold?: number;
  rows: {
    rank: number;
    rankTo?: number | null;
    cash: string;
    crowns: number;
    other?: string | null;
    profitBoostToken?: string | null;
    bonusBet?: string | null;
  }[];
}

type UpdatePayoutTemplateBody = CreatePayoutTemplateBody;

// API functions
export const getPayoutTemplates = async (tenantId: string): Promise<GetPayoutTemplatesResponse> => {
  return apiClient.get('/api/admin/tenants/{id}/payout-templates' as any, {
    pathParams: { id: tenantId },
  }) as any;
};

export const getPayoutTemplate = async (
  tenantId: string,
  templateId: string
): Promise<GetPayoutTemplateResponse> => {
  return apiClient.get('/api/admin/tenants/{id}/payout-templates/{templateId}' as any, {
    pathParams: { id: tenantId, templateId },
  }) as any;
};

export const createPayoutTemplate = async (
  tenantId: string,
  body: CreatePayoutTemplateBody
): Promise<GetPayoutTemplateResponse> => {
  return apiClient.post('/api/admin/tenants/{id}/payout-templates' as any, body, {
    pathParams: { id: tenantId },
  }) as any;
};

export const updatePayoutTemplate = async (
  tenantId: string,
  templateId: string,
  body: UpdatePayoutTemplateBody
): Promise<GetPayoutTemplateResponse> => {
  return apiClient.put('/api/admin/tenants/{id}/payout-templates/{templateId}' as any, body, {
    pathParams: { id: tenantId, templateId },
  }) as any;
};

export const deletePayoutTemplate = async (
  tenantId: string,
  templateId: string
): Promise<{ success: boolean }> => {
  return apiClient.delete('/api/admin/tenants/{id}/payout-templates/{templateId}' as any, {
    pathParams: { id: tenantId, templateId },
  }) as any;
};

// Query hooks
export function useGetPayoutTemplatesQuery(tenantId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.tenants.detail(tenantId || ''), 'payout-templates'] as const,
    queryFn: () => getPayoutTemplates(tenantId!),
    enabled: !!tenantId,
  });
}

export function useGetPayoutTemplateQuery(
  tenantId: string | undefined,
  templateId: string | undefined
) {
  return useQuery({
    queryKey: [
      ...queryKeys.tenants.detail(tenantId || ''),
      'payout-templates',
      templateId,
    ] as const,
    queryFn: () => getPayoutTemplate(tenantId!, templateId!),
    enabled: !!tenantId && !!templateId,
  });
}

// Mutation hooks
export function useCreatePayoutTemplateMutation(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreatePayoutTemplateBody) => createPayoutTemplate(tenantId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.tenants.detail(tenantId), 'payout-templates'] as const,
      });
      toast.success('Payout template created');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to create payout template');
    },
  });
}

export function useUpdatePayoutTemplateMutation(tenantId: string, templateId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdatePayoutTemplateBody) =>
      updatePayoutTemplate(tenantId, templateId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.tenants.detail(tenantId), 'payout-templates'] as const,
      });
      toast.success('Payout template updated');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update payout template');
    },
  });
}

export function useDeletePayoutTemplateMutation(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: string) => deletePayoutTemplate(tenantId, templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.tenants.detail(tenantId), 'payout-templates'] as const,
      });
      toast.success('Payout template deleted');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to delete payout template');
    },
  });
}
